import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { ThemeToggle } from "@/components/ThemeToggle";
import { Icon } from "@/components/ui/icon";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { PWAInstallButton } from "./PWAInstallButton";
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarSeparator,
} from "@/components/ui/sidebar";

interface AdminSidebarProps {
  activeTab: string;
  onTabChange: (tab: string) => void;
}

const analyticsItems = [
  { id: "analytics", label: "Analytics", icon: "analytics" },
  { id: "seo", label: "SEO", icon: "search" },
];

const resumeItems = [
  { id: "profile", label: "Profile", icon: "person" },
  { id: "competencies", label: "Competencies", icon: "psychology" },
  { id: "experience", label: "Experience", icon: "work" },
  { id: "skills", label: "Skills", icon: "construction" },
  { id: "projects", label: "Projects", icon: "folder" },
  { id: "education", label: "Education", icon: "school" },
];

const aiItems = [
  { id: "otto-history", label: "Otto Conversations", icon: "chat" },
  { id: "linkedin", label: "LinkedIn Posts", icon: "share" },
  { id: "blog", label: "Blog Generation", icon: "article", disabled: true },
  { id: "resume-gen", label: "Resume Generator", icon: "description", disabled: true },
];

export const AdminSidebar = ({ activeTab, onTabChange }: AdminSidebarProps) => {
  const navigate = useNavigate();

  const handleLogout = async () => {
    await supabase.auth.signOut();
    toast.success("Logged out successfully");
    navigate("/admin/login");
  };

  const handleViewLiveSite = () => {
    window.open("/", "_blank");
  };

  return (
    <Sidebar className="border-r border-sidebar-border">
      <SidebarHeader className="p-5 pb-4">
        <h1 className="text-lg font-bold text-sidebar-foreground">Resume Admin</h1>
        <p className="text-xs text-sidebar-foreground/60">Manage your content</p>
      </SidebarHeader>

      <SidebarSeparator className="opacity-50" />

      <SidebarContent className="px-2">
        <SidebarGroup>
          <SidebarGroupLabel className="text-[11px] uppercase tracking-wider text-sidebar-foreground/50 font-semibold mb-1">
            Analytics
          </SidebarGroupLabel>
          <SidebarGroupContent>
            <SidebarMenu>
              {analyticsItems.map((item) => (
                <SidebarMenuItem key={item.id}>
                  <SidebarMenuButton
                    isActive={activeTab === item.id}
                    onClick={() => onTabChange(item.id)}
                    tooltip={item.label}
                    className={activeTab === item.id ? "bg-sidebar-accent text-sidebar-accent-foreground font-medium" : ""}
                  >
                    <Icon name={item.icon} size={18} className={activeTab === item.id ? "text-sidebar-primary" : ""} />
                    <span>{item.label}</span>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              ))}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>

        <SidebarSeparator className="opacity-50 my-2" />

        <SidebarGroup>
          <SidebarGroupLabel className="text-[11px] uppercase tracking-wider text-sidebar-foreground/50 font-semibold mb-1">
            Edit Resume
          </SidebarGroupLabel>
          <SidebarGroupContent>
            <SidebarMenu>
              {resumeItems.map((item) => (
                <SidebarMenuItem key={item.id}>
                  <SidebarMenuButton
                    isActive={activeTab === item.id}
                    onClick={() => onTabChange(item.id)}
                    tooltip={item.label}
                    className={activeTab === item.id ? "bg-sidebar-accent text-sidebar-accent-foreground font-medium" : ""}
                  >
                    <Icon name={item.icon} size={18} className={activeTab === item.id ? "text-sidebar-primary" : ""} />
                    <span>{item.label}</span>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              ))}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>

        <SidebarSeparator className="opacity-50 my-2" />

        <SidebarGroup>
          <SidebarGroupLabel className="text-[11px] uppercase tracking-wider text-sidebar-foreground/50 font-semibold mb-1">
            AI Generation
          </SidebarGroupLabel>
          <SidebarGroupContent>
            <SidebarMenu>
              {aiItems.map((item) => (
                <SidebarMenuItem key={item.id}>
                  <SidebarMenuButton
                    isActive={activeTab === item.id}
                    onClick={() => !item.disabled && onTabChange(item.id)}
                    tooltip={item.label}
                    disabled={item.disabled}
                    className={`${item.disabled ? "opacity-40 cursor-not-allowed" : ""} ${activeTab === item.id ? "bg-sidebar-accent text-sidebar-accent-foreground font-medium" : ""}`}
                  >
                    <Icon name={item.icon} size={18} className={activeTab === item.id ? "text-sidebar-primary" : ""} />
                    <span>{item.label}</span>
                    {item.disabled && (
                      <span className="ml-auto text-[10px] bg-muted/80 text-muted-foreground px-1.5 py-0.5 rounded font-medium">
                        Soon
                      </span>
                    )}
                  </SidebarMenuButton>
                </SidebarMenuItem>
              ))}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>
      </SidebarContent>

      <SidebarFooter className="p-4 space-y-3 border-t border-sidebar-border/50">
        <div className="flex items-center justify-between px-1">
          <span className="text-xs text-sidebar-foreground/60 font-medium">Theme</span>
          <ThemeToggle />
        </div>
        
        <div className="flex items-center justify-between px-1">
          <span className="text-xs text-sidebar-foreground/60 font-medium">Install</span>
          <PWAInstallButton />
        </div>
        
        <Button
          variant="outline"
          size="sm"
          className="w-full justify-start gap-2"
          onClick={handleViewLiveSite}
        >
          <Icon name="open_in_new" size={16} />
          View Live Site
        </Button>

        <Button
          variant="ghost"
          size="sm"
          className="w-full justify-start gap-2 text-destructive hover:text-destructive hover:bg-destructive/10"
          onClick={handleLogout}
        >
          <Icon name="logout" size={16} />
          Logout
        </Button>
      </SidebarFooter>
    </Sidebar>
  );
};
